import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, FormControl, FormHelperText, TextField, Typography } from '@mui/material';
import plantumlEncoder from 'plantuml-encoder';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import {
	FieldErrors,
	UseFormRegister,
	UseFormSetError,
	useFieldArray,
	useForm,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Oval } from 'react-loader-spinner';
import { useSelector } from 'react-redux';

import { useAppDispatch } from 'store/store';
import { addChangesHistory, updateUseCases } from 'store/ticket-service/asyncActions';
import { getActiveTicket, getTicketUpdating } from 'store/ticket-service/selector';
import {
	setEditMode,
	setIsTicketUpdate,
	updateLocalTicket,
} from 'store/ticket-service/ticketSlice';
import {
	IDiagram,
	IKeyRolesData,
	IKeyRolesFormData,
	IParsedTableData,
	IParsedTableForm,
	IParsedTableFormData,
	ITableFormValue,
	IUsecaseData,
} from 'store/ticket-service/types';
import { setIsOpenNoDeleteAccess, triggerTicketSave } from 'store/user-service/userSlice';

import { flexMixin } from 'utils/flexMixin';
import { updateValueByPath } from 'utils/objectWithStringPath';
import { orderDiagrams } from 'utils/orderDiagrams';

import {
	HistoryActionTypes,
	NamespacesList,
	SharePermissions,
	TicketStages,
	UseCaseTableFields,
} from 'core/enums/common';
import { getUmlUrl } from 'core/helpers/get-uml-url';
import { errorNotify } from 'core/helpers/toasts';
import { editTitleSchema, editUseCasesSchema } from 'core/helpers/yupSchemas';

import { FormTitleValue } from 'pages/Home';

import { generateUseCaseDescriptionJSX } from 'components/TicketSteps/UsecasesStep/UseCaseTableBuilder';
import { ExpandableTextField } from 'components/shared/ExpandableTextField';

import { ReactComponent as EditDiagram } from 'assets/icons/edit-diagram.svg';
import { ReactComponent as Tick } from 'assets/icons/tick.svg';

interface FormValues {
	title: string;
	description: string;
	diagrams: Array<IDiagram>;
	tables: Array<IParsedTableForm>;
}

export interface EditConfig {
	registerInstance: UseFormRegister<FormValues>;
	errorsInstance: FieldErrors<FormValues>;
}

interface EditTicketFormProps {
	allPagesData: Array<IUsecaseData>;
	setPageData: (arg0: Array<IUsecaseData>) => void;
	pageData: IUsecaseData;
	pageIndex: number;
	ticketTitle: string;
	setTitleError: UseFormSetError<FormTitleValue>;
}

export const EditUsecasesStep: FC<EditTicketFormProps> = ({
	allPagesData,
	pageData,
	setPageData,
	pageIndex,
	ticketTitle,
	setTitleError,
}) => {
	const { t: useCasesTranslate, i18n } = useTranslation(NamespacesList['useCasesStep']);
	const { t: commonTranslate } = useTranslation(NamespacesList['common']);

	const translationConfig = useMemo(
		() => ({
			translate: useCasesTranslate,
			instance: i18n,
		}),
		[useCasesTranslate, i18n]
	);

	const dispatch = useAppDispatch();
	const activeTicket = useSelector(getActiveTicket);
	const updating = useSelector(getTicketUpdating);

	const [editDiagramModes, setEditDiagramModes] = useState<boolean[]>(
		new Array(pageData?.diagrams?.length || 0).fill(false)
	);
	const [parsedTable, setParsedTable] = useState<Record<string, any>>({});
	const [useCaseLoading, setUseCaseLoading] = useState<boolean>(true);
	const [initialData, setInitialData] = useState<FormValues>({
		title: '',
		description: '',
		tables: [],
		diagrams: [],
	});

	const toggleEditDiagramMode = (index: number) => {
		const updatedModes = [...editDiagramModes];
		updatedModes[index] = !updatedModes[index];
		setEditDiagramModes(updatedModes);
	};

	const {
		control,
		handleSubmit,
		register,
		reset,
		formState: { errors },
		getValues,
		setError,
	} = useForm<FormValues>({
		// @ts-ignore
		resolver: yupResolver(editUseCasesSchema()),
		defaultValues: {
			tables: [
				{
					table: {
						'Key Roles': [
							{
								Role: { value: '', isDeleted: false },
								Responsibilities: { value: '', isDeleted: false },
							},
						],
						Preconditions: [{ value: '', isDeleted: false }],
						Trigger: [{ value: '', isDeleted: false }],
						'Successful Outcome': [{ value: '', isDeleted: false }],
						'Main Scenario Flow of Events': [{ value: '', isDeleted: false }],
					},
				},
			],
			title: '',
			diagrams: [],
		},
	});

	const { fields: diagrams } = useFieldArray({
		control,
		name: 'diagrams',
	});

	useEffect(() => {
		if (activeTicket) {
			const parsedTable: IParsedTableData = JSON.parse(pageData.tables[0].table);

			const parsedTableKeyRoles = Array.isArray(parsedTable['Key Roles'])
				? parsedTable['Key Roles']
				: [];

			// support for older tickets
			const parsedTableFlowOfEvents = parsedTable?.['Main Scenario Flow of Events'] || [];

			const editFormTable = {
				'Key Roles':
					parsedTableKeyRoles?.map((item: IKeyRolesData) => ({
						Role: { value: item.Role, isDeleted: false },
						Responsibilities: { value: item.Responsibilities, isDeleted: false },
					})) ?? [],
				Preconditions:
					parsedTable['Preconditions']?.map(
						(item: string): ITableFormValue => ({ value: item, isDeleted: false })
					) ?? [],
				Trigger:
					parsedTable['Trigger']?.map(
						(item: string): ITableFormValue => ({ value: item, isDeleted: false })
					) ?? [],
				'Successful Outcome':
					parsedTable['Successful Outcome']?.map(
						(item: string): ITableFormValue => ({ value: item, isDeleted: false })
					) ?? [],
				'Main Scenario Flow of Events':
					parsedTableFlowOfEvents?.map(
						(item: string): ITableFormValue => ({ value: item, isDeleted: false })
					) ?? [],
			};

			setParsedTable(editFormTable);

			setInitialData({
				title: pageData?.title || '',
				description: pageData?.description || '',
				diagrams: orderDiagrams(pageData?.diagrams || []),
				tables: [{ table: editFormTable }],
			});

			reset({
				title: pageData?.title || '',
				description: pageData?.description || '',
				diagrams: orderDiagrams(pageData?.diagrams || []),
				tables: [
					{
						table: editFormTable,
					},
				],
			});
		}
	}, [activeTicket]);

	const currentStepPermission = useMemo(() => {
		const currentStagePermission = activeTicket?.permissions?.find(
			(permission) =>
				permission.ticketCurrentStep === TicketStages.useCases ||
				permission.ticketCurrentStep === null
		);

		if (currentStagePermission) return currentStagePermission.permission;

		return null;
	}, [activeTicket?.id, activeTicket?.permissions]);

	const updateTicketInfo = async (data: FormValues) => {
		const { title, description, diagrams, tables } = data;

		try {
			// validate each diagram code
			const updatedDiagrams = await validateDiagramsCode(diagrams);

			const formattedTable = parseFormTableData(tables[0].table);

			const updatedPage = {
				...pageData,
				title,
				description,
				diagrams: updatedDiagrams,
				tables: [{ table: JSON.stringify(formattedTable) }],
			};

			const updatedPageData = allPagesData.map((page, index) =>
				index + 1 === pageIndex ? updatedPage : page
			);

			setPageData(updatedPageData);

			dispatch(
				updateUseCases({
					useCaseId: pageData.id || '',
					useCaseData: {
						title,
						description,
						relatedUserStories: pageData.relatedUserStories,
						diagrams: updatedDiagrams,
						tables: [{ table: JSON.stringify(formattedTable), id: pageData.tables[0].id }],
					},
				})
			);

			handleTicketChanges({
				oldData: initialData,
				newData: {
					title: title,
					description,
					diagrams: updatedDiagrams,
					tables,
				},
			});

			editTitleSchema()
				.validate({ title: ticketTitle })
				.then((res) => {
					if (res) {
						if (activeTicket) {
							const id = activeTicket.id;
							dispatch(
								updateLocalTicket({
									id,
									title: ticketTitle,
								})
							);
						}

						if (!editDiagramModes.includes(true)) {
							saveTicketChanges();
						} else {
							errorNotify(
								commonTranslate('errors.canNotSwitchModeTitle'),
								commonTranslate('errors.canNotSwitchModeDescription')
							);
							return;
						}
					}
				})
				.catch((error) => {
					setTitleError('title', {
						type: 'manual',
						message: error.message,
					});
				});
		} catch (err) {
			console.log(err);
		}
	};

	const validateDiagramsCode = async (diagrams: Array<IDiagram>) => {
		let updatedDiagrams;
		let imageHasError = false;

		// check if diagrams are generated
		if (diagrams.filter((diagram) => diagram.pictureLink).length) {
			updatedDiagrams = await Promise.all(
				diagrams.map(async (diagram, index) => {
					const newCode = getValues(`diagrams.${index}.code`);

					if (newCode) {
						try {
							const response = await fetch(getUmlUrl(newCode));

							if (response.status === 200) {
								diagram.pictureLink = response.url;
							} else {
								setError(`diagrams.${index}.code`, {
									type: 'manual',
									message: commonTranslate('errors.failedToFetchUmlDescription') as string,
								});

								imageHasError = true;
							}
						} catch (error) {
							console.log(error);
						}
					}

					return diagram;
				})
			);
		} else {
			updatedDiagrams = diagrams;
		}

		if (!imageHasError) {
			return updatedDiagrams;
		} else {
			throw new Error('incorrect image syntax');
		}
	};

	const parseFormTableData = (table: IParsedTableFormData) => {
		// filter out values with isDeleted = true from each table category
		const updatedTable = Object.fromEntries(
			Object.entries(table).map(([key, value]) => [
				key,
				key !== UseCaseTableFields['Key Roles']
					? value.filter((item: ITableFormValue) => !item.isDeleted)
					: value.filter(
							(item: IKeyRolesFormData) => !(item.Role.isDeleted && item.Responsibilities.isDeleted)
					  ),
			])
		);

		// remove isDeleted field from each table category
		return Object.fromEntries(
			Object.entries(updatedTable).map(([key, value]) => [
				key,
				key !== UseCaseTableFields['Key Roles']
					? value.map((item: ITableFormValue) => item.value)
					: value.map((item: IKeyRolesFormData) => ({
							Role: item.Role.value,
							Responsibilities: item.Responsibilities.value,
					  })),
			])
		);
	};

	const handleTicketChanges = ({
		oldData,
		newData,
	}: {
		oldData: FormValues;
		newData: FormValues;
	}) => {
		if (activeTicket) {
			const oldTable = oldData.tables[0].table;
			const newTable = newData.tables[0].table;

			dispatch(
				addChangesHistory({
					userId: JSON.parse(localStorage.getItem('user') || '{}')?.id || '',
					ticketId: activeTicket.id,
					ticketStep: TicketStages.general,
					actionType: HistoryActionTypes.useCasesEdit,
					oldValue: JSON.stringify({
						title: oldData.title,
						description: oldData.description,
						table: oldTable,
						diagrams: oldData.diagrams,
					}),
					newValue: JSON.stringify({
						title: newData.title,
						description: newData.description,
						table: newTable,
						diagrams: newData.diagrams,
					}),
				})
			);
		}
	};

	const handleTableItemDelete = (path: string) => {
		if (currentStepPermission === null || currentStepPermission === SharePermissions.Manage) {
			const currentTable = getValues().tables[0].table;

			const formattedPath = `${path
				.replace('tables.0.table.', '')
				.replace('.value', '')}.isDeleted`;

			let updatedTable = updateValueByPath(currentTable, true, formattedPath);

			// Key roles must have both Role and Responsibilities. So, if we delete role, we delete responsibility
			if (path.includes(UseCaseTableFields['Key Roles']) && path.includes('Role')) {
				const responsibilitiesPath = formattedPath.replace('.Role.', '.Responsibilities.');
				updatedTable = updateValueByPath(updatedTable, true, responsibilitiesPath);
			}

			setParsedTable(updatedTable);

			reset({
				...getValues(),
				tables: [{ table: updatedTable }],
			});
		} else {
			dispatch(setIsOpenNoDeleteAccess(true));
		}
	};

	const handleTableItemRefresh = (path: string) => {
		const currentTable = getValues().tables[0].table;

		const formattedPath = `${path.replace('tables.0.table.', '').replace('.value', '')}.isDeleted`;
		let updatedTable = updateValueByPath(currentTable, false, formattedPath);

		// Key roles must have both Role and Responsibilities. So, if we delete role, we delete responsibility
		if (path.includes(UseCaseTableFields['Key Roles']) && path.includes('Role')) {
			const responsibilitiesPath = formattedPath.replace('.Role.', '.Responsibilities.');
			updatedTable = updateValueByPath(updatedTable, false, responsibilitiesPath);
		}

		setParsedTable(updatedTable);

		reset({
			...getValues(),
			tables: [{ table: updatedTable }],
		});
	};

	const handleResetEditForm = useCallback(() => {
		reset();
		dispatch(setEditMode(false));
	}, [reset]);

	const saveTicketChanges = () => {
		dispatch(setIsTicketUpdate(true));
		dispatch(triggerTicketSave());
		dispatch(setEditMode(false));
	};

	const saveNewPictureLink = async (index: number) => {
		const newCode = getValues(`diagrams.${index}.code`);

		// validate edited code and save new picture link. If code is invalid, show error message

		try {
			if (newCode) {
				const response = await fetch(getUmlUrl(newCode));

				if (response.status === 200) {
					const newPictureLink = response.url;
					const updatedDiagrams = [...diagrams];
					updatedDiagrams[index].pictureLink = newPictureLink;
					toggleEditDiagramMode(index);
				} else {
					setError(`diagrams.${index}.code`, {
						type: 'manual',
						message: commonTranslate('errors.failedToFetchUmlDescription') as string,
					});
				}
			}
		} catch (error) {
			console.log(error);
		}
	};

	useEffect(() => {
		// Due to the fast rendering, the UI blinking, so a loader was added to avoid this
		setTimeout(() => {
			setUseCaseLoading(false);
		}, 1000);
	}, []);

	return (
		<>
			<form
				className="rtl-able"
				style={{
					width: '100%',
					height: '100%',
					display: 'grid',
					gridTemplateRows: 'auto 0fr',
					overflow: 'auto',
				}}
				onSubmit={handleSubmit(updateTicketInfo)}
			>
				{pageData ? (
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							width: '99%',
							position: 'relative',
							overflow: 'auto',
							height: 'calc(100% - 4px)',
							padding: i18n?.language === 'ar' ? '0 12px 0 8px' : '0 8px 0 0',
							boxSizing: 'border-box',
						}}
					>
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'column',
								justifyContent: 'space-between',
								width: '100%',
								height: '100%',
							}}
						>
							<Box
								sx={{
									display: 'flex',
									flexDirection: 'column',
									gap: '16px',
								}}
							>
								<Box
									sx={{
										height: '100%',
									}}
								>
									<Box
										sx={{
											display: 'flex',
											alignItems: 'center',
											gap: '12px',
											marginBottom: '20px',
											width: '95%',
										}}
									>
										<Typography
											sx={{
												fontWeight: 700,
												whiteSpace: 'nowrap',
											}}
										>
											{useCasesTranslate('generalText.useCase')} #{pageIndex}:
										</Typography>
										<ExpandableTextField
											errors={errors.title}
											registerConfig={register(`title`)}
											isDisabled={false}
											defaultValue={pageData?.title || ''}
										/>
									</Box>
									<ExpandableTextField
										errors={errors.description}
										registerConfig={register(`description`)}
										isDisabled={false}
										defaultValue={pageData?.description || ''}
									/>

									<Box>
										{!useCaseLoading ? (
											generateUseCaseDescriptionJSX(
												parsedTable,
												handleTableItemDelete,
												handleTableItemRefresh,
												{
													registerInstance: register,
													errorsInstance: errors,
												},
												translationConfig
											)
										) : (
											<Box
												sx={{
													height: '300px',
													width: '100%',
													...flexMixin({}),
												}}
											>
												<Oval
													height={24}
													width={24}
													color="#fff"
													wrapperStyle={{}}
													wrapperClass=""
													visible={true}
													ariaLabel="oval-loading"
													secondaryColor="#91B6FF"
													strokeWidth={5}
													strokeWidthSecondary={5}
												/>
											</Box>
										)}
									</Box>

									{diagrams.filter((diagram) => diagram.pictureLink).length
										? diagrams.map((diagram, index) => (
												<Box
													key={index}
													sx={{
														display: 'flex',
														flexDirection: 'column',
														gap: '16px',
														padding: '16px 0',
													}}
												>
													<Typography
														sx={{
															fontWeight: 700,
															marginTop: { md: '8px', xl: '16px' },
														}}
													>
														{useCasesTranslate(`diagrams.${diagram.title}`)}
													</Typography>
													<Typography
														sx={{
															marginBottom: { md: '8px', xl: '20px' },
														}}
													>
														{useCasesTranslate(`diagrams.defaultDiagramDescription`)}
													</Typography>

													<Box
														sx={{
															marginTop: '20px',
															display: 'flex',
															justifyContent: 'center',
															position: 'relative',
														}}
													>
														{!editDiagramModes[index] ? (
															<Button
																type="button"
																sx={{
																	position: 'absolute',
																	top: '-25px',
																	right: i18n?.language === 'ar' ? 'unset' : '2px',
																	left: i18n?.language === 'ar' ? '2px' : 'unset',
																	minWidth: '24px',
																	height: '24px',
																	fontSize: '24px',
																	color: '#9FA6B3',
																}}
																onClick={() => toggleEditDiagramMode(index)}
															>
																<EditDiagram />
															</Button>
														) : (
															<Box
																sx={{
																	position: 'absolute',
																	top: '-25px',
																	right: i18n?.language === 'ar' ? 'unset' : '2px',
																	left: i18n?.language === 'ar' ? '2px' : 'unset',
																	display: 'flex',
																}}
															>
																<Button
																	type="button"
																	sx={{
																		minWidth: '24px',
																		height: '24px',
																		color: '#9FA6B3',
																	}}
																	onClick={() => saveNewPictureLink(index)}
																>
																	<Tick />
																</Button>

																<Button
																	type="button"
																	sx={{
																		minWidth: '24px',
																		height: '24px',
																		fontSize: '24px',
																		color: '#9FA6B3',
																	}}
																	onClick={() => toggleEditDiagramMode(index)}
																>
																	<span>&times;</span>
																</Button>
															</Box>
														)}

														{editDiagramModes[index] ? (
															<FormControl fullWidth margin="dense">
																<TextField
																	multiline
																	inputProps={{
																		maxLength: 10000,
																	}}
																	sx={{
																		'& .MuiOutlinedInput-root': {
																			borderRadius: '8px',
																			'& .css-1f449f3-MuiFormControl-root-MuiTextField-root': {
																				width: '100%',
																			},
																		},
																	}}
																	{...register(`diagrams.${index}.code`)}
																	defaultValue={
																		diagram.pictureLink
																			? plantumlEncoder.decode(
																					diagram.pictureLink.split('/').pop() ?? ''
																			  )
																			: ''
																	}
																	onInput={() => {
																		setError(`diagrams.${index}.code`, {
																			type: '',
																			message: '',
																		});
																	}}
																/>
																<FormHelperText
																	sx={{
																		fontSize: '12px',
																		textAlign: 'right',
																		marginRight: '12px',
																		color: 'red',
																		marginTop: '0',
																	}}
																>
																	{errors?.['diagrams']?.[index]?.['code']?.['message']}
																</FormHelperText>
															</FormControl>
														) : (
															<img
																style={{
																	maxWidth: '100%',
																}}
																alt="test"
																src={diagram.pictureLink}
															/>
														)}
													</Box>
												</Box>
										  ))
										: null}
								</Box>
							</Box>
						</Box>
					</Box>
				) : null}

				<Box
					className="not-rtl-able"
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						marginTop: 'auto',
					}}
				>
					<Button
						sx={{
							marginTop: '16px',
							height: '44px',
							width: '133px',
							borderRadius: '8px',
							textTransform: 'none',
							fontSize: '16px',
							fontWeight: '700',
						}}
						variant="outlined"
						color="primary"
						onClick={handleResetEditForm}
					>
						{commonTranslate('buttons.cancel')}
					</Button>

					{updating ? (
						<Button
							sx={{
								marginTop: '16px',
								height: '44px',
								width: '133px',
								borderRadius: '8px',
							}}
							type="submit"
							variant="contained"
							color="primary"
						>
							<Oval
								height={24}
								width={24}
								color="#fff"
								wrapperStyle={{}}
								wrapperClass=""
								visible={true}
								ariaLabel="oval-loading"
								secondaryColor="#91B6FF"
								strokeWidth={5}
								strokeWidthSecondary={5}
							/>
						</Button>
					) : (
						<>
							{pageData ? (
								<Button
									sx={{
										marginTop: '16px',
										height: '44px',
										width: '133px',
										borderRadius: '8px',
										textTransform: 'none',
										fontSize: '16px',
										fontWeight: '700',
									}}
									type="submit"
									variant="contained"
									color="primary"
									onClick={() => dispatch(setIsTicketUpdate(true))}
								>
									{commonTranslate('buttons.save')}
								</Button>
							) : null}
						</>
					)}
				</Box>
			</form>
		</>
	);
};
